import { Users } from 'lucide-react';
import type { WhosOutTodayEntry } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useWhosOutToday } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { PersonRow } from '../person-row';
import { ExpandableList } from '../expandable-list';
import { Chip } from '../chip';
import { asArray, formatRange } from '../widget-format';

/**
 * `whosOutToday` widget — everyone company-wide on approved leave today.
 * Rows are capped with a "+ N more" toggle (shared `ExpandableList`); each links to
 * the profile.
 */
export function WhosOutTodayWidget() {
  const { data, isLoading, isError } = useWhosOutToday();
  const formatName = useEmployeeNameFormatter();

  // Sort by the displayed name so ordering honors the Name Display setting.
  const sorted = data
    ? [...asArray(data)].sort((a, b) => formatName(a).localeCompare(formatName(b)))
    : [];
  const count = sorted.length;

  return (
    <WidgetCard
      title="Who's Out Today"
      icon={Users}
      action={
        !isLoading && !isError && count > 0 ? (
          <Chip color="gray">
            {count} {count === 1 ? 'person' : 'people'}
          </Chip>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load who&apos;s out today.
        </p>
      ) : sorted.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">No one is out today.</p>
      ) : (
        <ExpandableList
          items={sorted}
          getKey={(p) => p.id}
          renderItem={(p: WhosOutTodayEntry) => (
            <PersonRow
              employee={p}
              subline={
                <span className="inline-flex items-center gap-1.5">
                  {p.absenceTypeColor && (
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: p.absenceTypeColor }}
                    />
                  )}
                  {p.absenceTypeName} · {formatRange(p.startDate, p.endDate)}
                </span>
              }
            />
          )}
        />
      )}
    </WidgetCard>
  );
}
